/**
 * The grant dialog's **target set** and the reversal dialog's **reclaim offer
 * set** (grant-dialog-monetary-bonus-target, design D3/D4/D7).
 *
 * A pure module, like `provisionOptions.ts`: the console has no
 * component-rendering harness worth trusting these rules to, so the rules that
 * decide *which bucket money lands in* live here and are unit-tested directly.
 *
 * Three things carry the weight:
 *
 * 1. **Monetary targets exist at all.** The shipped dialog filtered
 *    `asset_kind === 'virtual'`, so an operator who meant "$10" could only
 *    reach a virtual bucket — which platform balance, the spend gate, and
 *    `deduct_waterfall` structurally never see, those resolving on
 *    `(asset_kind, currency)` groups that are never summed.
 * 2. **The default is an attribute rule, never a name list.** `bonus_cash` is
 *    one tenant's configuration datum, not a platform constant. The default is
 *    the active monetary bucket of smallest catalogue `spending_priority`, ties
 *    broken by ascending `type`. It is therefore only obtainable from a
 *    catalogue-joined set — `defaultGrantTarget` refuses an uncatalogued one,
 *    so no caller can accidentally preselect the first bucket *by name*.
 * 3. **The reclaim offer fails closed.** An admin `adjust` debit can drain a
 *    real-money wallet, so a monetary bucket is offered as a reclaim target
 *    only when all six conditions of the `Bonus Reversal Input Contract`
 *    predicate hold. What that establishes is stated as exactly that and no
 *    more: it does not prove the balance's provenance.
 */

import { formatAmount } from './amounts'
import type { SubAccountRow, SubAccountTypeRow } from './accountsApi'

/** The lifecycle status a grantable bucket must have; the server refuses others. */
const ACTIVE = 'active'

/** The platform's default top-up target (`confirm_topup`'s own default). */
export const DEFAULT_TOPUP_TYPE_KEY = 'cash'

export interface GrantTarget {
  id: string
  type: string
  assetKind: string
  currency: string | null
  balance: string
  /** The catalogue's priority, or `null` when the type is not catalogued. */
  spendingPriority: number | null
  /** True when no catalogue entry matched — never a guessed number. */
  priorityUnknown: boolean
  /** The catalogue's fail-closed declaration, or `null` when uncatalogued. */
  appCreditable: boolean | null
  /** The catalogue's stored column, or `null` when uncatalogued. */
  withdrawable: boolean | null
}

/**
 * A target set plus the one bit that decides whether a default may be derived
 * from it: whether it was joined against a real catalogue answer.
 *
 * Carrying it in the value (rather than leaving it to the caller) is what makes
 * "no preselection before the catalogue's first 200" structural instead of a
 * convention: `defaultGrantTarget` has nothing to work from otherwise.
 */
export interface GrantTargetSet {
  targets: readonly GrantTarget[]
  catalogued: boolean
}

/** The `(asset_kind, currency)` shape `formatAmount` and the unit rules read. */
export function targetAsset(target: GrantTarget): {
  asset_kind: string
  currency: string | null
} {
  return { asset_kind: target.assetKind, currency: target.currency }
}

function catalogueIndex(
  types: readonly SubAccountTypeRow[]
): Map<string, SubAccountTypeRow> {
  const index = new Map<string, SubAccountTypeRow>()
  for (const type of types) index.set(type.type_key, type)
  return index
}

/**
 * Order within one asset-kind group: catalogued buckets by ascending
 * `spending_priority` then ascending `type`, then uncatalogued buckets by
 * ascending `type`, last.
 *
 * With no catalogue (`catalogued: false`) every bucket is uncatalogued, so the
 * order degrades to ascending `type` — presentation only, never a default.
 */
function compareWithinGroup(a: GrantTarget, b: GrantTarget): number {
  if (a.priorityUnknown !== b.priorityUnknown) return a.priorityUnknown ? 1 : -1
  if (!a.priorityUnknown && !b.priorityUnknown) {
    const pa = a.spendingPriority as number
    const pb = b.spendingPriority as number
    if (pa !== pb) return pa - pb
  }
  return a.type < b.type ? -1 : a.type > b.type ? 1 : 0
}

function groupRank(target: GrantTarget): number {
  return target.assetKind === 'monetary' ? 0 : 1
}

/**
 * The account's `active` buckets of **both** asset kinds, joined to the
 * catalogue, grouped monetary-then-virtual and ordered deterministically.
 *
 * `types` is `null` while the catalogue is unresolved or failed: the set is
 * still built (the account's own sub-accounts decide which targets exist, not
 * the catalogue) but every entry is flagged `priorityUnknown` and the set is
 * marked uncatalogued, which is what forbids a default.
 *
 * Returns new objects; neither input is mutated.
 */
export function buildGrantTargets(
  subAccounts: readonly SubAccountRow[],
  types: readonly SubAccountTypeRow[] | null
): GrantTargetSet {
  const index = types === null ? null : catalogueIndex(types)
  const targets = subAccounts
    .filter((bucket) => bucket.status === ACTIVE)
    .map((bucket) => {
      const entry = index?.get(bucket.type)
      return {
        id: bucket.id,
        type: bucket.type,
        assetKind: bucket.asset_kind,
        currency: bucket.currency,
        balance: bucket.balance,
        spendingPriority: entry ? entry.spending_priority : null,
        priorityUnknown: !entry,
        appCreditable: entry ? entry.app_creditable === true : null,
        withdrawable: entry ? entry.withdrawable === true : null,
      }
    })
    .sort((a, b) => groupRank(a) - groupRank(b) || compareWithinGroup(a, b))
  return { targets, catalogued: types !== null }
}

/**
 * The one-time default: the first monetary target under the catalogue order,
 * else the first virtual one, else `null`.
 *
 * Returns `null` for an uncatalogued set **by construction**: the rule's only
 * input is `spending_priority`, which lives in the catalogue, so a caller
 * without it has no default — not a name-ordered stand-in. With `a_cash` at
 * priority `100` and `z_bonus` at `50`, a stand-in would have left `a_cash`
 * selected forever, which is the opposite of the specified rule.
 */
export function defaultGrantTarget(set: GrantTargetSet): string | null {
  if (!set.catalogued) return null
  const monetary = set.targets.find((target) => target.assetKind === 'monetary')
  if (monetary) return monetary.id
  return set.targets[0]?.id ?? null
}

/** A monetary target is acknowledged before it can be granted into (D6). */
export function requiresMonetaryAcknowledgement(
  target: GrantTarget | null
): boolean {
  return target != null && target.assetKind === 'monetary'
}

export interface GrantSubmitGate {
  /** True once a submission has been sent: the fields are locked. */
  locked: boolean
}

/**
 * Whether the grant may be submitted, mirroring `canSubmitProvision`'s shape.
 *
 * No target ⇒ no unit ⇒ no amount ⇒ no submission: the dialog opens in that
 * state and stays there until the operator picks a target or the catalogue's
 * first success preselects one.
 */
export function canSubmitGrant(
  target: GrantTarget | null,
  acknowledged: boolean,
  gate: GrantSubmitGate = { locked: false }
): boolean {
  if (gate.locked) return false
  if (target === null) return false
  if (requiresMonetaryAcknowledgement(target) && !acknowledged) return false
  return true
}

/** Option text: the type, the asset kind, the currency, and the priority. */
export function describeGrantTarget(target: GrantTarget): string {
  const kind =
    target.assetKind === 'monetary'
      ? `monetary · ${target.currency ?? '?'}`
      : 'virtual'
  const priority = target.priorityUnknown
    ? '优先级未知'
    : `优先级 ${target.spendingPriority}`
  return `${target.type} · ${kind} · ${priority} · ${formatAmount(
    target.balance,
    targetAsset(target)
  )}`
}

// ---------------------------------------------------------------------------
// Path B: which monetary buckets a directed reclaim debit may be offered for
// ---------------------------------------------------------------------------

export type ReclaimExclusion =
  | 'not-active-monetary'
  | 'uncatalogued'
  | 'not-app-creditable'
  | 'withdrawable'
  | 'default-topup-type'
  | 'no-later-same-currency-bucket'

export interface ReclaimCandidate {
  target: GrantTarget
  offered: boolean
  /** Why it is not offered — one reason per failing condition, in order. */
  exclusions: readonly ReclaimExclusion[]
}

export interface ReclaimTargetSet {
  /** Every `active` monetary bucket, with its verdict — offered or not. */
  candidates: readonly ReclaimCandidate[]
  offered: readonly GrantTarget[]
  /** True when the catalogue answered; a failed catalogue offers nothing. */
  catalogued: boolean
}

/**
 * The six-condition offer predicate, evaluated per `active` monetary bucket.
 *
 * The conjunction is deliberate and is not reducible to any one condition.
 * What it establishes, exactly: the bucket's type is one the tenant declared
 * bonus-creditable, is not withdrawable, is not the platform's default top-up
 * target, and is spent before at least one same-currency bucket. It does NOT
 * establish that the balance never came from a top-up — which sub-account type
 * a tenant's top-ups credit is per-order metadata this console never reads.
 *
 * An uncatalogued bucket, and every bucket while the catalogue is unresolved or
 * failed, fails closed: not offered.
 */
export function buildReclaimTargets(
  subAccounts: readonly SubAccountRow[],
  types: readonly SubAccountTypeRow[] | null
): ReclaimTargetSet {
  const set = buildGrantTargets(subAccounts, types)
  const monetary = set.targets.filter((target) => target.assetKind === 'monetary')

  // Condition 6's reference point: the largest `spending_priority` among the
  // account's active, CATALOGUED monetary buckets of the same currency.
  const largestByCurrency = new Map<string, number>()
  for (const target of monetary) {
    if (target.priorityUnknown || target.currency == null) continue
    const current = largestByCurrency.get(target.currency)
    const priority = target.spendingPriority as number
    if (current === undefined || priority > current) {
      largestByCurrency.set(target.currency, priority)
    }
  }

  const candidates = monetary.map((target) => {
    const exclusions: ReclaimExclusion[] = []
    if (target.priorityUnknown) {
      exclusions.push('uncatalogued')
    } else {
      if (target.appCreditable !== true) exclusions.push('not-app-creditable')
      if (target.withdrawable !== false) exclusions.push('withdrawable')
      if (target.type === DEFAULT_TOPUP_TYPE_KEY) exclusions.push('default-topup-type')
      const largest =
        target.currency == null ? undefined : largestByCurrency.get(target.currency)
      if (largest === undefined || (target.spendingPriority as number) >= largest) {
        exclusions.push('no-later-same-currency-bucket')
      }
    }
    return { target, offered: exclusions.length === 0, exclusions }
  })

  return {
    candidates,
    offered: candidates.filter((c) => c.offered).map((c) => c.target),
    catalogued: set.catalogued,
  }
}

/**
 * Whether the reversal dialog would offer a directed reclaim for this exact
 * bucket — the same predicate, run on one target, for the grant dialog's
 * acknowledgement sentence (D6/D7).
 */
export function isReclaimableFromDialog(
  target: GrantTarget | null,
  subAccounts: readonly SubAccountRow[],
  types: readonly SubAccountTypeRow[] | null
): boolean {
  if (target === null || target.assetKind !== 'monetary') return false
  const set = buildReclaimTargets(subAccounts, types)
  return set.offered.some((offered) => offered.id === target.id)
}

/**
 * The acknowledgement's reclaim sentence — per target, by the predicate above.
 *
 * It never promises a reversal-dialog entry point the selected target will not
 * have: a `cash` bucket, a withdrawable one, one whose type carries no
 * `app_creditable` declaration, an uncatalogued one, or the account's only
 * same-currency monetary bucket is told the truth — the admin `adjust` API is
 * the only way back.
 */
export function reclaimStatement(reclaimable: boolean): string {
  if (reclaimable) {
    return (
      '「回收赠送」永远无法收回本笔赠金（其候选集只含虚拟桶，对任何调用者都是如此）；' +
      '可在「回收赠送」弹窗的「定向回收 monetary 桶」路径对该桶发起 adjust debit 收回。'
    )
  }
  return (
    '「回收赠送」永远无法收回本笔赠金（其候选集只含虚拟桶，对任何调用者都是如此）；' +
    '本弹窗也不为该桶提供任何回收入口，收回只能直接调用管理员 adjust API。'
  )
}

/** The two facts the acknowledgement states alongside the reclaim path. */
export const MONETARY_LEDGER_MARKER_NOTE =
  '本笔赠送仍会以运营赠送标记写入流水，并出现在运营台账本的「赠送」预设视图中。'

export const MONETARY_NO_EVENT_NOTE =
  'monetary 赠送不会发出 account.bonus_granted 对外通知，下游应用只能通过读取余额感知。'

/** The standing note every virtual option carries (D3). */
export const VIRTUAL_DOWNSTREAM_NOTE =
  '下游平台的余额、消费闸门与扣减瀑布都按 (asset_kind, currency) 解析，' +
  '赠入虚拟桶的余额可能对下游完全不可见、也永远不会被消费。'
